/**
 * Sanitize-schema + entity-tag preprocessing helpers, split out of
 * `entityRendering.tsx` so callers that only need the sanitize schema do
 * NOT pull in `createEntityMarkdownComponents` (which imports
 * `EntityBadgeById`, which transitively loops back through the entity-type
 * registry).
 *
 * Why this exists:
 *   `entityRendering.tsx` (createEntityMarkdownComponents) → EntityBadgeById
 *     → HoverPreviewBadge → hoverBodyDispatch → entityTypeRegistry → …
 *     → MeetingNotesDetailSection (registered as the meeting-notes summary)
 *     → ../../markdown/entityRendering (for entitySanitizeSchema)
 *
 *   That cycle puts `entitySanitizeSchema` in the TDZ during module init
 *   and crashes the slideout with
 *   "Cannot access 'entitySanitizeSchema' before initialization".
 *
 *   The split breaks the cycle: registry-reachable sections import
 *   `entitySanitizeSchema` from THIS file, which has no badge / registry
 *   imports.
 *
 * `entityRendering.tsx` re-exports these symbols so existing callers
 * outside the registry chain keep working unchanged.
 */

import { defaultSchema } from 'rehype-sanitize';

import {
  MEETING_SLOT_TAG_WITH_ID_RE,
  SLOT_ID_RE,
} from '@/lib/email/meetingSlotTag';

// Wrap any BARE entity UUID as a data-entity span (issue #9925) so the markdown
// renderer turns it into an entity badge. Runs LAST in preprocessEntityTags,
// after <entity>->span conversion, so real entity ids are already inside a
// data-entity span. Alternation is tried left-to-right; every branch except
// `uuid` returns the match unchanged (skip), `uuid` wraps:
//   entitySpan - a data-entity span BLOCK -> skip (idempotency: protects the id
//      attribute AND the inner-text UUID as one unit). Bounded to 20 000 chars to
//      prevent worse-than-linear backtracking on malformed/unclosed input (same
//      defensive pattern as inlineCode's 500-char cap). Content beyond the bound
//      falls through to the uuid branch and gets wrapped — accepted trade-off;
//      legitimate entity display names are never that long.
//   linkTarget - a markdown link TARGET `](...)` -> skip ONLY the `](url)` target,
//      not the whole link. Protects the UUID in [x](ahoy:contact:<uuid>) targets
//      (wrapping it there would corrupt the link) while still scanning link TEXT,
//      so a bare UUID appearing as visible link text `[<uuid>](url)` becomes a
//      badge. Anchors on `]` so only real link targets are skipped, never
//      arbitrary `(...)` in prose. The destination matcher tolerates ONE level of
//      balanced parens and backslash-escaped chars — `[t](https://…/Foo_(bar)#<uuid>)`
//      — so a `)` inside the URL before the UUID no longer ends the match early
//      and leave the UUID exposed to wrapping. The three alternatives are disjoint
//      on their first char (non-paren/non-backslash, `\`, `(`), so the repetition
//      stays linear — no catastrophic backtracking.
//   refLinkDef - a reference-style link DEFINITION `[label]: destination` -> skip
//      the whole `[label]: destination` prefix (destination is a bare token, no
//      whitespace), protecting a UUID inside the destination of
//      `[label]: ahoy:contact:<uuid>`. Anchored to line start (`m` flag) with up
//      to 3 leading spaces, matching CommonMark's link-definition syntax, so only
//      real definitions are skipped — an inline `array[0]:` in prose (mid-line)
//      is never mistaken for one.
//   htmlTag - any HTML tag `<...>` -> skip (protects UUIDs living in attributes,
//      e.g. href)
//   fencedCode - a fenced code BLOCK ```...``` OR ~~~...~~~ -> skip (prevents
//      injecting span markup into opaque literal code content, which the
//      markdown parser never reparses for HTML; leaked tags would render as raw
//      visible characters, not badges). Both CommonMark fence styles are
//      handled: the two inner alternatives each match a same-delimiter
//      open/close pair (backtick-open never closes on a tilde fence and vice
//      versa). The lazy middle absorbs an optional language tag after the
//      opening fence. Bounded to 20 000 chars: an unclosed fence whose content
//      stays within the bound is correctly protected (UUID not wrapped); an
//      unclosed fence whose content exceeds the bound falls through to the
//      uuid branch and gets wrapped — accepted trade-off, matching the
//      500-char accepted trade-off for inlineCode.
//   inlineCode - an inline code SPAN `...` -> skip (same rationale as fenced
//      blocks; bounded to 500 chars as defensive hardening against pathological
//      single-line input).
//   uuid - a bare UUID -> wrap. Inner text = original UUID (fallbackDisplayName
//      -> neutral pill on unresolved, never the red "Unavailable" pill).
const BARE_ENTITY_UUID_RULE =
  /(?<entitySpan><span\b[^>]*\bdata-entity=["']?true["']?[^>]*>[\s\S]{0,20000}?<\/span>)|(?<linkTarget>\]\((?:[^()\\]|\\.|\([^()]*\))*\))|(?<refLinkDef>^[ \t]{0,3}\[[^\]\n]+\]:[ \t]*\S+)|(?<htmlTag><[^>]+>)|(?<fencedCode>```[\s\S]{0,20000}?```|~~~[\s\S]{0,20000}?~~~)|(?<inlineCode>`[^`\n]{0,500}`)|\b(?<uuid>[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/gim;

const wrapBareEntityUuids = (input: string): string =>
  input.replace(BARE_ENTITY_UUID_RULE, (match: string, ...args: unknown[]) => {
    const groups = args[args.length - 1] as { uuid?: string } | undefined;
    return groups?.uuid
      ? `<span data-entity="true" id="${groups.uuid}">${groups.uuid}</span>`
      : match;
  });

/**
 * Normalize a content string's entity-mention tags to a single
 * <span data-entity="true" ...> form that the ReactMarkdown `span` renderer
 * (see `createEntityMarkdownComponents` in entityRendering.tsx) intercepts.
 * Also wraps any bare entity UUIDs (issue #9925) into data-entity spans so the
 * renderer turns them into inline entity badges, skipping UUIDs already inside
 * entity spans, inline markdown link targets `](...)`, reference-style link
 * definitions `[label]: destination`, HTML tags, fenced code blocks, and inline
 * code spans — link TEXT is still scanned, so a bare UUID shown as visible link
 * text becomes a badge.
 *
 * Handles four upstream formats:
 *   - <Entity id="..." type="...">Name</Entity>          (current Plate
 *                                                          output, see
 *                                                          mentionUtils.ts::
 *                                                          serializeMention)
 *   - <NamedEntity id="..." type="...">Name</NamedEntity> (older XML mention)
 *   - <mention id="..." type="...">Name</mention>         (older XML mention)
 *   - <@uuid|Display Name>                                (legacy
 *                                                          Slack-style)
 *
 * All XML variants use either id="..." or entity-id="..." for the entity
 * ID; we rename entity-id="..." → id="..." up front because the downstream
 * span renderer and the sanitize schema (see entitySanitizeSchema below)
 * only know about `id`. Without renaming, rehype-sanitize strips the
 * attribute and the MentionBadge renders with no id, regressing
 * navigation/click behaviour.
 */
export const preprocessEntityTags = (content: string): string => {
  const normalized = content
    .replace(
      /<(Entity|NamedEntity|mention)\b([^>]*?)>([\s\S]{0,20000}?)<\/\1>/gi,
      (_match, _tagName: string, attrs: string, name: string) => {
        const normalizedAttrs = attrs.replace(/\bentity-id=/gi, 'id=');
        return `<entity${normalizedAttrs}>${name}</entity>`;
      }
    )
    .replace(
      /<@([^|>]+)\|([^>]+)>/g,
      '<entity type="ahoy_user" id="$1">$2</entity>'
    );

  // Convert all <entity> tags to data-attributed <span> elements.
  // ReactMarkdown's `span` component renderer (createEntityMarkdownComponents)
  // intercepts these and emits MentionBadge.
  //
  // Order matters: the self-closing and attribute-less opening-tag patterns
  // must match BEFORE the attributed opening-tag pattern, otherwise the
  // attributed pattern would partially match `<entity>` and leave behind a
  // malformed `<entity>...</span>` (opening tag with no attrs, closing tag
  // rewritten). The attribute-less case is unlikely in practice (upstream
  // tags carry an entity-id), but handling it defensively keeps the
  // serializer output valid no matter the input shape.
  //
  // The self-closing pattern anchors on `\b` (word boundary) rather than
  // `\s` so it matches both `<entity foo="bar"/>` AND the attribute-less
  // `<entity/>`. With `\s` the attribute-less form would slip through
  // unchanged and leak a stray `<entity/>` into the output.
  //
  // <meeting-slot id="UUID"/> is emitted by IdCodec when the referenced id
  // has type="meeting_slot" (see apps/reasoning/services/id_codec.py). It is
  // NOT a CRM entity, so it must NOT flow through the entity→EntityBadgeById
  // pipeline. Convert it to a data-meeting-slot span so the span renderer
  // (createEntityMarkdownComponents) can render the resolved time instead of
  // a broken badge. Self-closing is the canonical form; paired
  // <meeting-slot>...</meeting-slot> is handled by the same matcher.
  //
  // The transform is ATOMIC per tag (MEETING_SLOT_TAG_WITH_ID_RE matches the
  // whole self-closing OR paired tag at once), so an opening tag is only ever
  // rewritten together with its own closing tag. A previous approach rewrote
  // openings and closings in two independent passes, which left a mismatched
  // `<meeting-slot ...>…</span>` whenever the opening was left unchanged (a
  // non-UUID id) but the blanket closing pass still ran. The matcher's `\sid=`
  // also means a `data-id`/`aria-id` attribute is never captured as the slot id
  // (no spurious "Meeting time" badge). A missing id, a non-UUID id, or a bare
  // opening carries nothing to resolve and is left untouched for the normal
  // sanitizer/text path.
  const withSpans = normalized
    .replace(/<entity\b([^>]*?)\/>/gi, '<span data-entity="true"$1></span>')
    .replace(/<entity>/gi, '<span data-entity="true">')
    .replace(/<entity\s/gi, '<span data-entity="true" ')
    .replace(/<\/entity>/gi, '</span>')
    .replace(
      MEETING_SLOT_TAG_WITH_ID_RE,
      (match, id: string, inner: string | undefined) => {
        if (!SLOT_ID_RE.test(id)) {
          return match;
        }
        // `inner` is undefined for the self-closing (and bare-opening) form and
        // the paired form's folded text otherwise; re-emit it as a SIBLING
        // AFTER the (empty) slot span so author content next to the chip is
        // preserved. It must NOT nest inside the slot span: the span renderer
        // (createEntityMarkdownComponents' data-meeting-slot branch) renders
        // only the resolved-time badge and ignores its children, so any inner
        // content placed inside the span would vanish. Emitting it as a sibling
        // matches the backend send-path substitution (substitute_slot_ref
        // appends the folded text after the rendered time).
        return inner === undefined
          ? `<span data-meeting-slot="true" id="${id}"></span>`
          : `<span data-meeting-slot="true" id="${id}"></span>${inner}`;
      }
    );

  return wrapBareEntityUuids(withSpans);
};

/**
 * Strip `<entity>` tags from content, preserving the display name inside.
 * Use for plain-text contexts (list rows, titles) where markdown rendering
 * is not appropriate but raw tags would leak through.
 *
 * `\b[^>]*` (not `\s[^>]*`) so attribute-less tags emitted by
 * `preprocessEntityTags` for an input without attributes — `<entity>Name</entity>`,
 * `<entity/>` — also strip cleanly.
 */
export const stripEntityTags = (content: string): string =>
  content
    // Self-closing <entity .../> → empty string (no display name)
    .replace(/<entity\b[^>]*\/>/gi, '')
    // Opening+closing <entity ...>name</entity> → preserve display name
    .replace(/<entity\b[^>]*>([\s\S]{0,20000}?)<\/entity>/gi, '$1');

/**
 * Strip every supported mention/entity tag format from content, preserving
 * each tag's display name. Handles raw upstream formats (Entity / NamedEntity /
 * mention / Slack-style `<@uuid|Name>`) AND the normalized `<entity>` form so
 * the helper is safe to call before OR after `preprocessEntityTags`.
 *
 * Use for plain-text previews (pipeline card excerpts, list-row snippets)
 * where the raw mention markup would otherwise leak through to the user.
 *
 * Normalized `<entity>` patterns use `\b[^>]*` rather than `\s[^>]*` so an
 * attribute-less `<entity>Name</entity>` (which `preprocessEntityTags` can
 * emit when the source tag had no attributes) also strips cleanly.
 */
export const stripAllMentionFormats = (content: string): string =>
  content
    // Raw XML mention formats (self-closing) → drop
    .replace(/<(Entity|NamedEntity|mention)\b[^>]*\/>/gi, '')
    // Raw XML mention formats (paired) → preserve display name
    .replace(
      /<(Entity|NamedEntity|mention)\b[^>]*>([\s\S]{0,20000}?)<\/\1>/gi,
      '$2'
    )
    // Slack-style <@uuid|Display Name> → preserve display name
    .replace(/<@[^|>]+\|([^>]+)>/g, '$1')
    // Normalized <entity> form (self-closing) → drop
    .replace(/<entity\b[^>]*\/>/gi, '')
    // Normalized <entity> form (paired) → preserve display name
    .replace(/<entity\b[^>]*>([\s\S]{0,20000}?)<\/entity>/gi, '$1')
    // preprocessEntityTags() output (`<span data-entity="true" ...>Name</span>`).
    // Required so this helper is safe to call BEFORE or AFTER preprocessing —
    // without it, callers that strip after preprocessing leak span markup
    // into plain-text previews.
    .replace(/<span\b[^>]*\bdata-entity=["']?true["']?[^>]*\/>/gi, '')
    .replace(
      /<span\b[^>]*\bdata-entity=["']?true["']?[^>]*>([\s\S]{0,20000}?)<\/span>/gi,
      '$1'
    );

/**
 * rehype-sanitize schema extended with the attributes our entity tags rely
 * on (data-entity / type / id on spans, className on div/p, the ahoy://
 * protocol on hrefs). Pass this as the second element of rehype-sanitize's
 * plugin tuple.
 */
export const entitySanitizeSchema = {
  ...defaultSchema,
  // `<u>` is not in rehype-sanitize's GitHub default schema, but the rich
  // text note editor (PlateNoteEditor) serializes its underline mark as
  // `<u>`. Purely presentational, so allowing it is safe.
  tagNames: [...(defaultSchema.tagNames || []), 'u'],
  attributes: {
    ...(defaultSchema.attributes || {}),
    span: [
      ...(defaultSchema.attributes?.['span'] || []),
      'dataEntity',
      'dataMeetingSlot',
      'type',
      'id',
    ],
    // Allow remark-github-alerts class attributes through sanitizer.
    // Note: the alert's octicon glyph is an inline `style="--oct-icon:url(…)"`
    // on a span; sanitize strips inline style by design (style passthrough is
    // an overlay/exfiltration vector), so sanitized alerts render box + title
    // + body without the small icon. That has always been the behavior on
    // sanitized surfaces — do not "fix" it by allowlisting `style`.
    div: [...(defaultSchema.attributes?.['div'] || []), 'className'],
    p: [...(defaultSchema.attributes?.['p'] || []), 'className'],
  },
  protocols: {
    ...(defaultSchema.protocols || {}),
    href: [...(defaultSchema.protocols?.href || []), 'ahoy'],
  },
};
